// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import Plan from "./plan.js"
import PlanResourcePeriod from "./plan-resource-period.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "plan_id": {
      "column": {
        "default": null,
        "name": "plan_id",
        "null": false,
        "type": null
      },
      "name": "plan_id",
      "selected_by_default": null,
      "translated": null
    },
    "resource_name": {
      "column": {
        "default": null,
        "name": "resource_name",
        "null": false,
        "type": "string"
      },
      "name": "resource_name",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "PlanResource",
  "collectionKey": "plan_resources",
  "collectionName": "plan_resources",
  "i18nKey": "plan_resource",
  "camelizedLower": "planResource",
  "name": "PlanResource",
  "nameDasherized": "plan-resource",
  "pluralName": "plan_resources",
  "ransackable_associations": [
    {
      "className": "PlanResource::Audit",
      "collectionName": "plan_resource_audits",
      "foreignKey": "plan_resource_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "PlanResourceAudit",
      "through": null
    },
    {
      "className": "PlanResource::Audit",
      "collectionName": "plan_resource_audits",
      "foreignKey": "plan_resource_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "PlanResourceAudit",
      "through": null
    },
    {
      "className": "PlanResourcePeriod",
      "collectionName": "plan_resource_periods",
      "foreignKey": "plan_resource_id",
      "name": "periods",
      "macro": "has_many",
      "resource_name": "PlanResourcePeriod",
      "through": null
    },
    {
      "className": "Plan",
      "collectionName": "plans",
      "foreignKey": "plan_id",
      "name": "plan",
      "macro": "belongs_to",
      "resource_name": "Plan",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "plan_id",
      "column": {
        "default": null,
        "name": "plan_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "resource_name",
      "column": {
        "default": null,
        "name": "resource_name",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "PlanResourcePeriod",
      "collectionName": "plan_resource_periods",
      "foreignKey": "plan_resource_id",
      "name": "periods",
      "macro": "has_many",
      "resource_name": "PlanResourcePeriod",
      "through": null
    },
    {
      "className": "Plan",
      "collectionName": "plans",
      "foreignKey": "plan_id",
      "name": "plan",
      "macro": "belongs_to",
      "resource_name": "Plan",
      "through": null
    }
  ],
  "paramKey": "plan_resource",
  "primaryKey": "id"
}

/** Frontend model for PlanResource. */
export default class PlanResource extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {any} */
  planId() {
    return this.readAttributeUnderscore("plan_id")
  }

  /** @returns {boolean} */
  hasPlanId() {
    const value = this.planId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  resourceName() {
    return this.readAttributeUnderscore("resource_name")
  }

  /** @returns {boolean} */
  hasResourceName() {
    const value = this.resourceName()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./plan-resource-period.js").default>} */
  periods() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["plan_resource_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "periods",
        model: this,
        modelClass: PlanResourcePeriod
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./plan-resource-period.js").default>>} */
  loadPeriods() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["plan_resource_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "periods",
        model: this,
        modelClass: PlanResourcePeriod
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./plan-resource-period.js").default>>} */
  ensurePeriodsLoaded() {
    return this.ensureAssociationLoaded("periods")
  }

  /** @returns {import("./plan.js").default | null} */
  plan() {
    return this._readBelongsToReflection({modelClass: Plan, reflectionName: "plan"})
  }

  /** @returns {Promise<import("./plan.js").default | null>} */
  loadPlan() {
    if (!("planId" in this)) throw new Error("Foreign key method wasn't defined: planId")

    const id = this.planId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "plan",
        model: this,
        modelClass: Plan
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./plan.js").default | null>} */
  ensurePlanLoaded() {
    return this.ensureAssociationLoaded("plan")
  }
}
